// src/feature-module/student/StudentDashboard.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaChartLine,
  FaGraduationCap,
  FaCertificate,
  FaRobot,
  FaUsers,
  FaPencilAlt,
  FaFileContract,
  FaClipboardList,
  FaBook,
  FaBookOpen,
  FaCreditCard,
  FaClock,
  FaFileAlt,
  FaMicrophone,
  FaHeadphones,
  FaTrophy,
  FaLanguage,
  FaFeatherAlt,
  FaIdCard,
  FaLock,
} from "react-icons/fa";

type CardKey =
  | "batch"
  | "courses"
  | "hw"
  | "assistant"
  | "attendance"
  | "progress"
  | "writing"
  | "writingTask"
  | "quiz"
  | "regulation"
  | "tracking"
  | "homework"
  | "vocabulary"
  | "exams"
  | "classTests"
  | "coverLetter"
  | "cvBuilder"
  | "shadowing"
  | "audio";

type DashboardCard = {
  key: CardKey;
  title: string;
  description: string;
  icon: JSX.Element;
  link: string;
  gradient: string;
  iconBg: string;
  iconColor: string;
};

const StudentDashboard: React.FC = () => {
  const { student } = useAuth();

  const [pendingWritingCount, setPendingWritingCount] = useState(0);
  const [pendingQuizCount, setPendingQuizCount] = useState(0);
  const [nextWritingDeadline, setNextWritingDeadline] = useState<string | null>(
    null,
  );
  const [nextQuizDeadline, setNextQuizDeadline] = useState<string | null>(null);
  const [pendingHomeworkCount, setPendingHomeworkCount] = useState(0);
  const [nextHomeworkDeadline, setNextHomeworkDeadline] = useState<
    string | null
  >(null);
  const [pendingShadowingCount, setPendingShadowingCount] = useState(0);
  const [nextShadowingDeadline, setNextShadowingDeadline] = useState<
    string | null
  >(null);

  // ✅ Course Regulations pending state
  const [pendingRegulationsCount, setPendingRegulationsCount] = useState(0);
  const [nextRegulationDeadline, setNextRegulationDeadline] = useState<
    string | null
  >(null);
  const [regulationDaysUntilDeadline, setRegulationDaysUntilDeadline] =
    useState<number | null>(null);

  const [activeClassTestCount, setActiveClassTestCount] = useState(0);
  const [accessLocked, setAccessLocked] = useState<
    Partial<Record<CardKey, boolean>>
  >({});

  // Installment deadline state
  const [installmentDeadline, setInstallmentDeadline] = useState<{
    hasUpcoming: boolean;
    installmentNumber?: number;
    amount?: number;
    dueDate?: string;
    daysUntilDue?: number;
    courseTitle?: string;
    urgency?: string;
  } | null>(null);

  const API = process.env.REACT_APP_API_BASE_URL;
  const isInactive = !!student && student.isActiveStudent === false;

  const needsActive: Record<CardKey, boolean> = {
    batch: true,
    // Not gated: an inactive student's only path to reactivating is paying an
    // installment here, so this page must stay reachable while locked out.
    courses: false,
    hw: true,
    assistant: true,
    attendance: true,
    progress: true,
    writing: true,
    writingTask: true,
    quiz: true,
    regulation: false,
    tracking: true,
    homework: true,
    vocabulary: true,
    exams: true,
    classTests: true,
    coverLetter: true,
    cvBuilder: true,
    shadowing: true,
    audio: true,
  };

  // ✅ Get regulation card styling based on urgency
  const getRegulationCardStyle = () => {
    if (pendingRegulationsCount === 0) {
      // No pending - use default teal style
      return {
        gradient: "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200",
        iconBg: "bg-teal-100",
        iconColor: "text-teal-600",
        animation: "", // No animation
      };
    }

    if (regulationDaysUntilDeadline === null) {
      // Has pending but no deadline info - use default
      return {
        gradient: "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200",
        iconBg: "bg-teal-100",
        iconColor: "text-teal-600",
        animation: "",
      };
    }

    // Determine urgency level
    if (regulationDaysUntilDeadline < 0) {
      // Overdue - RED with strong pulse + shake
      return {
        gradient: "bg-gradient-to-br from-red-100 to-rose-100 border-red-400",
        iconBg: "bg-red-200",
        iconColor: "text-red-700",
        animation: "animate-pulse-glow-shake", // Strongest animation
      };
    } else if (regulationDaysUntilDeadline === 0) {
      // Today - RED with pulse + glow
      return {
        gradient: "bg-gradient-to-br from-red-100 to-orange-100 border-red-300",
        iconBg: "bg-red-200",
        iconColor: "text-red-600",
        animation: "animate-pulse-glow", // Strong animation
      };
    } else if (regulationDaysUntilDeadline <= 1) {
      // 1 day - ORANGE with pulse
      return {
        gradient:
          "bg-gradient-to-br from-orange-100 to-amber-100 border-orange-300",
        iconBg: "bg-orange-200",
        iconColor: "text-orange-700",
        animation: "animate-pulse-soft", // Medium animation
      };
    } else if (regulationDaysUntilDeadline <= 2) {
      // 2 days - AMBER with gentle pulse
      return {
        gradient:
          "bg-gradient-to-br from-amber-100 to-yellow-100 border-amber-300",
        iconBg: "bg-amber-200",
        iconColor: "text-amber-700",
        animation: "animate-pulse-gentle", // Gentle animation
      };
    } else {
      // 3+ days - default teal
      return {
        gradient: "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200",
        iconBg: "bg-teal-100",
        iconColor: "text-teal-600",
        animation: "",
      };
    }
  };

  // Fetch installment deadline
  useEffect(() => {
    const fetchInstallmentDeadline = async () => {
      try {
        const response = await fetch(
          `${API}/api/student/installments/next-deadline`,
          {
            credentials: "include",
          },
        );
        if (response.ok) {
          const data = await response.json();
          setInstallmentDeadline(data);
        }
      } catch (error) {
        console.error("Failed to fetch installment deadline:", error);
      }
    };

    fetchInstallmentDeadline();
  }, [API]);

  // ✅ Fetch pending course regulations
  useEffect(() => {
    if (isInactive) return;

    const loadPendingRegulations = async () => {
      try {
        const response = await fetch(
          `${API}/api/student/regulations/pending-info`,
          {
            credentials: "include",
          },
        );
        if (!response.ok) return;

        const data = await response.json();
        setPendingRegulationsCount(data.count || 0);

        if (data.earliestDeadline) {
          const deadline = new Date(data.earliestDeadline);
          setNextRegulationDeadline(
            deadline.toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
            }),
          );

          // ✅ Calculate days until deadline
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const deadlineDate = new Date(data.earliestDeadline);
          deadlineDate.setHours(0, 0, 0, 0);
          const daysUntil = Math.ceil(
            (deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          );
          setRegulationDaysUntilDeadline(daysUntil);
        }
      } catch (error) {
        console.error("Failed to fetch pending regulations:", error);
        setPendingRegulationsCount(0);
        setNextRegulationDeadline(null);
        setRegulationDaysUntilDeadline(null);
      }
    };

    loadPendingRegulations();
  }, [API, isInactive]);

  // Homework (NEW system)
  useEffect(() => {
    if (isInactive) return;

    const loadPendingHomework = async () => {
      try {
        const batchRes = await fetch(`${API}/api/student/my-batch`, {
          credentials: "include",
        });
        if (!batchRes.ok) return;
        const batch = await batchRes.json();
        const batchId = batch?.id ?? batch?.batchId;

        if (!batchId) return;

        const homeworkRes = await fetch(
          `${API}/api/student/homework/my-batch?batchId=${batchId}`,
          { credentials: "include" },
        );
        if (!homeworkRes.ok) return;
        const data = await homeworkRes.json();
        const homework = Array.isArray(data) ? data : [];

        const now = new Date();
        const pending = homework.filter((hw: any) => {
          if (hw.completed) return false;
          if (!hw.deadline) return true;
          const d = new Date(hw.deadline);
          return d >= now;
        });

        setPendingHomeworkCount(pending.length);

        if (pending.length > 0) {
          const nearest = pending
            .filter((hw: any) => hw.deadline)
            .map((hw: any) => new Date(hw.deadline))
            .sort((a: Date, b: Date) => a.getTime() - b.getTime())[0];
          if (nearest) {
            setNextHomeworkDeadline(
              nearest.toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
              }),
            );
          }
        }
      } catch {
        setPendingHomeworkCount(0);
        setNextHomeworkDeadline(null);
      }
    };

    loadPendingHomework();
  }, [API, isInactive]);

  // ✅ Shadowing tasks
  useEffect(() => {
    if (isInactive) return;

    const loadPendingShadowing = async () => {
      try {
        const batchRes = await fetch(`${API}/api/student/my-batch`, {
          credentials: "include",
        });
        if (!batchRes.ok) return;
        const batch = await batchRes.json();
        const batchId = batch?.id ?? batch?.batchId;

        if (!batchId) return;

        const shadowingRes = await fetch(
          `${API}/api/student/shadowing-tasks/my-batches/${batchId}`,
          { credentials: "include" },
        );
        if (!shadowingRes.ok) return;
        const data = await shadowingRes.json();
        const tasks = Array.isArray(data) ? data : [];

        const now = new Date();
        const pending = tasks.filter((task: any) => {
          if (task.completed) return false;
          if (!task.deadline) return true;
          const d = new Date(task.deadline);
          return d >= now;
        });

        setPendingShadowingCount(pending.length);

        if (pending.length > 0) {
          const nearest = pending
            .filter((t: any) => t.deadline)
            .map((t: any) => new Date(t.deadline))
            .sort((a: Date, b: Date) => a.getTime() - b.getTime())[0];
          if (nearest) {
            setNextShadowingDeadline(
              nearest.toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
              }),
            );
          }
        }
      } catch {
        setPendingShadowingCount(0);
        setNextShadowingDeadline(null);
      }
    };

    loadPendingShadowing();
  }, [API, isInactive]);

  // Writing tasks
  useEffect(() => {
    if (isInactive) return;

    const isPendingWritingTask = (t: {
      completed?: boolean;
      deadline?: string;
    }) => {
      if (t.completed) return false;
      if (!t.deadline) return true;
      const d = new Date(t.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d >= today;
    };

    const loadPendingWriting = async () => {
      try {
        const batchRes = await fetch(`${API}/api/student/my-batch`, {
          credentials: "include",
        });
        if (!batchRes.ok) return;
        const batch = await batchRes.json();
        const batchId = batch?.id ?? batch?.batchId;
        const studentId = student?.studentId;
        if (!batchId || !studentId) return;

        const tasksRes = await fetch(
          `${API}/api/student/writing-tasks/my-batch?batchId=${batchId}&studentId=${studentId}`,
          { credentials: "include" },
        );
        if (!tasksRes.ok) return;
        const data = await tasksRes.json();
        const tasks = Array.isArray(data) ? data : [];

        const pendingTasks = tasks.filter(isPendingWritingTask);
        setPendingWritingCount(pendingTasks.length);

        if (pendingTasks.length > 0) {
          const nearest = pendingTasks
            .filter((t) => t.deadline)
            .map((t) => new Date(t.deadline))
            .sort((a, b) => a.getTime() - b.getTime())[0];
          if (nearest) {
            setNextWritingDeadline(
              nearest.toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
              }),
            );
          }
        }
      } catch {
        setPendingWritingCount(0);
        setNextWritingDeadline(null);
      }
    };

    loadPendingWriting();
  }, [API, isInactive, student?.studentId]);

  // Quizzes
  useEffect(() => {
    if (isInactive) {
      setPendingQuizCount(0);
      setNextQuizDeadline(null);
      return;
    }

    const loadPendingQuizzes = async () => {
      try {
        const res = await fetch(`${API}/api/student/my-batch/quizzes`, {
          credentials: "include",
        });
        if (!res.ok) return;

        const raw = await res.json().catch(() => []);
        const quizzes = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.content)
            ? raw.content
            : [];

        const now = new Date();
        const pending = quizzes.filter((q: any) => {
          if (q.completed) return false;
          if (!q.deadline) return true;
          const d = new Date(q.deadline);
          return d >= now;
        });

        setPendingQuizCount(pending.length);

        if (pending.length > 0) {
          const nearest = pending
            .filter((q: { deadline: any }) => q.deadline)
            .map(
              (q: { deadline: string | number | Date }) => new Date(q.deadline),
            )
            .sort(
              (a: { getTime: () => number }, b: { getTime: () => number }) =>
                a.getTime() - b.getTime(),
            )[0];
          if (nearest) {
            setNextQuizDeadline(
              nearest.toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
              }),
            );
          }
        }
      } catch {
        setPendingQuizCount(0);
        setNextQuizDeadline(null);
      }
    };

    loadPendingQuizzes();
  }, [API, isInactive]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/student/cover-letter/check-access`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(`${API}/api/student/cv-builder/check-access`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]).then(([clData, cvData]) => {
      setAccessLocked({
        coverLetter: clData
          ? !(clData.hasAusbildungAccess || clData.hasFSJAccess)
          : false,
        cvBuilder: cvData ? !cvData.hasAccess : false,
      });
    });
  }, [API]);

  useEffect(() => {
    if (isInactive) return;
    fetch(`${API}/api/student/class-tests/my-tests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: any[]) => {
        const active = Array.isArray(data)
          ? data.filter((t) => t.effectiveStatus === "ACTIVE").length
          : 0;
        setActiveClassTestCount(active);
      })
      .catch(() => {});
  }, [API, isInactive]);

  const cards: DashboardCard[] = [
    {
      key: "batch",
      title: "My Batch",
      description: "See your batch, mentor, schedule, and progress.",
      icon: <FaUsers size={24} />,
      link: "/student/my-batch",
      gradient:
        "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
    },
    {
      key: "courses",
      title: "My Courses & Payments",
      description: "Enrolled courses, installments, and payments.",
      icon: <FaCreditCard size={24} />,
      link: "/student/my-courses",
      gradient: "bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
    },
    {
      key: "progress",
      title: "My Course Progress",
      description: "Track your learning progress.",
      icon: <FaChartLine size={24} />,
      link: "/student/course-progress",
      gradient: "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200",
      iconBg: "bg-sky-100",
      iconColor: "text-sky-600",
    },
    {
      key: "regulation",
      title: "Course Regulations",
      description: "Download, sign and upload course regulations.",
      icon: <FaFileContract size={24} />,
      link: "/student/course-regulations",
      gradient: "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200",
      iconBg: "bg-teal-100",
      iconColor: "text-teal-600",
    },
    {
      key: "quiz",
      title: "Manage Quizzes",
      description: "Take quizzes and view your results.",
      icon: <FaGraduationCap size={24} />,
      link: "/student/manage-quizzes",
      gradient: "bg-gradient-to-br from-red-50 to-rose-50 border-red-200",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
    },
    {
      key: "writingTask",
      title: "Manage Writing Task",
      description: "Practice German writing with AI feedback.",
      icon: <FaPencilAlt size={24} />,
      link: "/student/my-writing-tasks",
      gradient: "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
    },
    {
      key: "shadowing",
      title: "Shadowing Practice",
      description:
        "Listen, shadow, and record German audio for speaking practice.",
      icon: <FaMicrophone size={24} />,
      link: "/student/my-shadowing-tasks",
      gradient: "bg-gradient-to-br from-orange-50 to-red-50 border-orange-200",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
    },
    {
      key: "hw",
      title: "My Homework (Hausaufgabe)",
      description: "Complete homework with multiple tasks and get AI feedback.",
      icon: <FaBook size={24} />,
      link: "/student/my-homework",
      gradient: "bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200",
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
    },
    {
      key: "attendance",
      title: "My Attendance",
      description: "Check your class attendance records.",
      icon: <FaCertificate size={24} />,
      link: "/student/my-attendance",
      gradient:
        "bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200",
      iconBg: "bg-violet-100",
      iconColor: "text-violet-600",
    },
    {
      key: "assistant",
      title: "My Study Assistant",
      description: "Ask questions and get instant help.",
      icon: <FaRobot size={24} />,
      link: "/student/study-assistant",
      gradient: "bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200",
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
    },
    {
      key: "writing",
      title: "Writing Practice",
      description: "Practice German writing with AI corrections.",
      icon: <FaFeatherAlt size={24} />,
      link: "/student/writing-practice",
      gradient: "bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      key: "tracking",
      title: "Application Tracker",
      description: "Track your applications.",
      icon: <FaClipboardList size={24} />,
      link: "/student/ausbildung-application-tracking",
      gradient: "bg-gradient-to-br from-green-50 to-lime-50 border-green-200",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
    },
    {
      key: "vocabulary",
      title: "My Vocabulary Learning",
      description:
        "Learn German vocabulary with daily flashcards and track your progress.",
      icon: <FaLanguage size={24} />,
      link: "/student/vocabulary",
      gradient:
        "bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-300",
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-700",
    },
    {
      key: "exams",
      title: "My Exams",
      description: "View assigned exams and take tests",
      icon: <FaTrophy size={24} />,
      link: "/student/my-exams",
      gradient:
        "bg-gradient-to-br from-fuchsia-50 to-pink-50 border-fuchsia-300",
      iconBg: "bg-fuchsia-100",
      iconColor: "text-fuchsia-700",
    },
    {
      key: "classTests",
      title: "My Class Tests",
      description: "View and take chapter class tests assigned to your batch",
      icon: <FaBookOpen size={24} />,
      link: "/student/my-class-tests",
      gradient: "bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-300",
      iconBg: "bg-cyan-100",
      iconColor: "text-cyan-700",
    },
    {
      key: "audio",
      title: "Speaking Practice",
      description:
        "Record or upload audio and receive feedback from your mentor.",
      icon: <FaHeadphones size={24} />,
      link: "/student/speaking-practice",
      gradient: "bg-gradient-to-br from-lime-50 to-green-50 border-lime-200",
      iconBg: "bg-lime-100",
      iconColor: "text-lime-700",
    },
    {
      key: "coverLetter",
      title: "Cover Letter Generator",
      description:
        "Generate professional German cover letters for Ausbildung applications.",
      icon: <FaFileAlt size={24} />,
      link: "/student/cover-letter-generator",
      gradient:
        "bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300",
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-700",
    },
    {
      key: "cvBuilder",
      title: "Auto-CV Builder",
      description:
        "Build your German Lebenslauf with a live preview and download as PDF.",
      icon: <FaIdCard size={24} />,
      link: "/student/cv-builder",
      gradient: "bg-gradient-to-br from-sky-50 to-blue-50 border-sky-300",
      iconBg: "bg-sky-100",
      iconColor: "text-sky-700",
    },
  ];

  const handleBlockedClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    alert(
      "Your account is inactive. Please clear dues to access this section.",
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Custom animations for Course Regulations card urgency states */}
      <style>{`
        @keyframes pulse-gentle {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          }
          50% {
            transform: scale(1.01);
            box-shadow: 0 10px 15px -3px rgb(251 191 36 / 0.2), 0 4px 6px -4px rgb(251 191 36 / 0.2);
          }
        }
        
        @keyframes pulse-soft {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 10px 15px -3px rgb(249 115 22 / 0.3), 0 4px 6px -4px rgb(249 115 22 / 0.3);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          }
          50% {
            transform: scale(1.03);
            box-shadow: 0 0 20px rgb(239 68 68 / 0.4), 0 10px 25px -3px rgb(239 68 68 / 0.3);
          }
        }
        
        @keyframes pulse-glow-shake {
          0%, 100% {
            transform: scale(1) translateX(0);
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          }
          25% {
            transform: scale(1.03) translateX(-2px);
            box-shadow: 0 0 25px rgb(220 38 38 / 0.5), 0 10px 30px -3px rgb(220 38 38 / 0.4);
          }
          50% {
            transform: scale(1.04) translateX(0);
            box-shadow: 0 0 30px rgb(220 38 38 / 0.5), 0 10px 35px -3px rgb(220 38 38 / 0.4);
          }
          75% {
            transform: scale(1.03) translateX(2px);
            box-shadow: 0 0 25px rgb(220 38 38 / 0.5), 0 10px 30px -3px rgb(220 38 38 / 0.4);
          }
        }
        
        .animate-pulse-gentle {
          animation: pulse-gentle 3s ease-in-out infinite;
        }
        
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 1.5s ease-in-out infinite;
        }
        
        .animate-pulse-glow-shake {
          animation: pulse-glow-shake 1s ease-in-out infinite;
        }
      `}</style>

      {/* Header */}
      <PageHeader
        icon={<FaGraduationCap />}
        title="Student Dashboard"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Dashboard" }]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-8 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2">
            {student?.firstName
              ? `Welcome, ${student.firstName} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white opacity-90 text-base">
            Here you can see all your learning helping tools.
          </p>
        </div>

        {/* Installment Deadline Banner */}
        {installmentDeadline?.hasUpcoming && (
          <InstallmentBanner deadline={installmentDeadline} />
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => {
            const locked = isInactive && needsActive[card.key];
            const isQuiz = card.key === "quiz";
            const isWriting = card.key === "writingTask";
            const isHomework = card.key === "hw";
            const isRegulation = card.key === "regulation";
            const isShadowing = card.key === "shadowing";
            const isClassTests = card.key === "classTests";

            const showBadge =
              (isQuiz && pendingQuizCount > 0) ||
              (isWriting && pendingWritingCount > 0) ||
              (isHomework && pendingHomeworkCount > 0) ||
              (isRegulation && pendingRegulationsCount > 0) ||
              (isShadowing && pendingShadowingCount > 0) ||
              (isClassTests && activeClassTestCount > 0);

            const count = isQuiz
              ? pendingQuizCount
              : isWriting
                ? pendingWritingCount
                : isHomework
                  ? pendingHomeworkCount
                  : isRegulation
                    ? pendingRegulationsCount
                    : pendingShadowingCount;

            const date = isQuiz
              ? nextQuizDeadline
              : isWriting
                ? nextWritingDeadline
                : isHomework
                  ? nextHomeworkDeadline
                  : isRegulation
                    ? nextRegulationDeadline
                    : nextShadowingDeadline;

            // ✅ Get dynamic styling for regulation card
            const regulationStyle = isRegulation
              ? getRegulationCardStyle()
              : null;
            const cardGradient = isRegulation
              ? regulationStyle!.gradient
              : card.gradient;
            const cardIconBg = isRegulation
              ? regulationStyle!.iconBg
              : card.iconBg;
            const cardIconColor = isRegulation
              ? regulationStyle!.iconColor
              : card.iconColor;
            const cardAnimation = isRegulation
              ? regulationStyle!.animation
              : "";

            const accessIsLocked = !!accessLocked[card.key];

            return (
              <div key={card.key} className="relative">
                {accessIsLocked && !locked && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-gray-800/80 text-white rounded-md px-2 py-1 text-xs font-semibold shadow">
                    <FaLock size={9} />
                    Locked
                  </div>
                )}
                <Link
                  to={locked ? "#" : card.link}
                  onClick={locked ? handleBlockedClick : undefined}
                  className={[
                    "block group rounded-xl shadow-md border transition-all duration-200 overflow-hidden h-full flex flex-col hover:shadow-xl hover:-translate-y-0.5",
                    locked ? "opacity-60 cursor-not-allowed" : "",
                    cardGradient,
                    cardAnimation,
                  ].join(" ")}
                >
                  <div className="p-6 flex-1">
                    <div
                      className={`w-12 h-12 mb-4 ${cardIconBg} ${cardIconColor} rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm`}
                    >
                      {card.icon}
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">
                      {card.title}
                    </h2>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {card.description}
                    </p>

                    {showBadge &&
                      (isClassTests ? (
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-red-500 text-white rounded-lg px-2.5 py-1 shadow-md">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          <span className="text-xs font-bold tracking-wide">
                            LIVE
                          </span>
                        </div>
                      ) : (
                        <div className="absolute top-3 right-3 bg-amber-100 text-amber-800 border border-amber-300 rounded-lg px-2.5 py-1 text-center shadow-md">
                          <div className="text-xs font-semibold">
                            {count} Pending
                          </div>
                          {date && (
                            <div className="text-[10px] font-semibold mt-0.5 opacity-90">
                              D– {date}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

// Installment Deadline Banner Component
const InstallmentBanner: React.FC<{
  deadline: {
    installmentNumber?: number;
    amount?: number;
    dueDate?: string;
    daysUntilDue?: number;
    courseTitle?: string;
    urgency?: string;
  };
}> = ({ deadline }) => {
  const { amount, dueDate, daysUntilDue, courseTitle, urgency } = deadline;

  const getBannerStyle = () => {
    switch (urgency) {
      case "OVERDUE":
        return {
          bg: "bg-red-300",
          border: "border-red-400",
          icon: "text-red-700",
          text: "text-gray-900",
          badge: "bg-red-400 text-red-900",
        };
      case "URGENT":
        return {
          bg: "bg-red-200",
          border: "border-red-400",
          icon: "text-red-600",
          text: "text-gray-800",
          badge: "bg-red-300 text-red-900",
        };
      case "WARNING":
        return {
          bg: "bg-amber-100",
          border: "border-amber-300",
          icon: "text-amber-600",
          text: "text-gray-800",
          badge: "bg-amber-200 text-amber-800",
        };
      default:
        return {
          bg: "bg-blue-100",
          border: "border-blue-300",
          icon: "text-blue-600",
          text: "text-gray-800",
          badge: "bg-blue-200 text-blue-800",
        };
    }
  };

  const style = getBannerStyle();

  const getMessage = () => {
    if (urgency === "OVERDUE") {
      return {
        title: "Payment Deadline Missed",
        message: `You missed the installment payment deadline. Please contact with G2G to resolve this issue and continue accessing your course.`,
        badge: "Overdue",
      };
    } else if (urgency === "URGENT") {
      return {
        title: "Urgent - Payment Reminder",
        message: `Your installment payment is due in ${daysUntilDue} ${
          daysUntilDue === 1 ? "day" : "days"
        }. If you miss the payment deadline, you will not be able to continue the course until all outstanding dues are paid in full.`,
        badge: `${daysUntilDue} days left`,
      };
    } else if (urgency === "WARNING") {
      return {
        title: "Upcoming Payment",
        message: `You have an installment payment due in ${daysUntilDue} days.`,
        badge: `${daysUntilDue} days remaining`,
      };
    } else {
      return {
        title: "Installment Reminder",
        message: `Your next installment payment is coming up.`,
        badge: `Due soon`,
      };
    }
  };

  const content = getMessage();
  const formattedAmount = new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

  const formattedDate = dueDate
    ? new Date(dueDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <div
      className={`mb-6 ${style.bg} border ${style.border} rounded-lg p-4 sm:p-5 shadow-sm`}
    >
      {/* Header Section - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className={`${style.icon} flex-shrink-0 mt-0.5`}>
            <FaClock size={18} className="sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900">
              {content.title}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 leading-relaxed">
              {content.message}
            </p>
          </div>
        </div>
        <span
          className={`${style.badge} px-2.5 sm:px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap self-start sm:self-auto`}
        >
          {content.badge}
        </span>
      </div>

      {/* Details Section - Responsive */}
      <div className="flex flex-col gap-3 pt-3 border-t border-gray-200">
        {/* Info Grid - Stacks on mobile, inline on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 font-medium">Amount:</span>
            <span className="font-bold text-gray-900">{formattedAmount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 font-medium">Due Date:</span>
            <span className="font-semibold text-gray-900">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 font-medium">Course:</span>
            <span className="font-medium text-gray-700 truncate">
              {courseTitle}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
